import { generateRandomString } from './string';
import Toastify from 'toastify-js';
import type { Options } from 'toastify-js';

const toastify = (message: string, duration = 3000, type = 'info') => {
	const toastId = generateRandomString(5);
	let toast = '';
	switch (type) {
		case 'success':
			toast = `
            <div class="toast toast-${toastId} toast-top toast-end">
                <div class="alert bg-info-200 border-info-600">
                <span class="text-white">${message}</span>
                </div>
            </div>`;
			break;
		case 'error':
			toast = `
            <div class="toast toast-${toastId} toast-top toast-end">
                <div class="alert bg-red-200 border-red-600">
                <span class="text-white">${message}</span>
                </div>
            </div>`;
			break;
		case 'info':
			toast = `
            <div class="toast toast-${toastId} toast-top toast-end">
                <div class="alert">
                <span class="text-black">${message}</span>
                </div>
            </div>`;
			break;
	}

	document.querySelector('.drawer')?.insertAdjacentHTML('beforeend', toast);
	if (duration !== 0) {
		setTimeout(() => {
			document.querySelector(`.toast.toast-${toastId}`)?.remove();
		}, duration);
	}

	return toastId;
};

const toastifyV2 = (message: string, submessage?: string, duration = 3000, type = 'info') => {
	const toastId = generateRandomString(5);
	let toast = '';
	const transitionClass = 'transition-all duration-300 opacity-0 translate-y-4';

	switch (type) {
		case 'success':
			toast = `
            <div class="absolute top-5 end-5 ${transitionClass}" id="toast-${toastId}">
                <div class="bg-green-50 border border-green-200 text-sm text-green-800 rounded-lg p-4" role="alert" tabindex="-1" aria-labelledby="hs-with-list-label">
                    <div class="flex">
                        <div class="shrink-0">
                            <span class="inline-flex justify-center items-center size-8 rounded-full border-4 border-green-100 bg-green-200 text-teal-800">
                                <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M18 6 6 18"></path>
                                    <path d="m6 6 12 12"></path>
                                </svg>
                            </span>
                        </div>
                        <div class="ms-5 flex items-center">
                            <h3 id="hs-bordered-red-style-label" class="text-teal-800 font-semibold text-base">${message}</h3>
                            <p class="text-xs text-teal-800 ${submessage ? 'block' : 'hidden'}">${submessage}</p>
                        </div>
                    </div>
                </div>
            </div>`;
			break;
		case 'error':
			toast = `
            <div class="absolute top-5 end-5 ${transitionClass}" id="toast-${toastId}">
                <div class="bg-red-50 border border-red-200 text-sm text-red-800 rounded-lg p-4" role="alert" tabindex="-1" aria-labelledby="hs-with-list-label">
                    <div class="flex">
                        <div class="shrink-0">
                            <span class="inline-flex justify-center items-center size-8 rounded-full border-4 border-rose-200 bg-rose-300 text-white">
                                <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M18 6 6 18"></path>
                                    <path d="m6 6 12 12"></path>
                                </svg>
                            </span>
                        </div>
                        <div class="ms-5 flex items-center">
                            <h3 id="hs-bordered-red-style-label" class="text-rose-700 font-semibold text-base">${message}</h3>
                            <p class="text-xs text-rose-700 ${submessage ? 'block' : 'hidden'}">${submessage}</p>
                        </div>
                    </div>
                </div>
            </div>`;
			break;
	}

	const root = document.querySelector('#root');
	root?.insertAdjacentHTML('beforeend', toast);
	const toastElement = document.querySelector(`#toast-${toastId}`);

	// Apply opening transition
	setTimeout(() => {
		toastElement?.classList.remove('opacity-0', 'translate-y-4');
		toastElement?.classList.add('opacity-100', 'translate-y-0');
	}, 10);

	// Set timeout for closing and transition
	if (duration !== 0) {
		setTimeout(() => {
			// Apply closing transition
			toastElement?.classList.remove('opacity-100', 'translate-y-0');
			toastElement?.classList.add('opacity-0', 'translate-y-4');

			// Remove from DOM after transition
			setTimeout(() => {
				toastElement?.remove();
			}, 300); // Match the transition duration
		}, duration);
	}

	return toastId;
};

type configNotification = {
	text: string;
	position?: 'left-top' | 'center-top' | 'right-top' | 'left-bottom' | 'center-bottom' | 'right-bottom';
	variant?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'light' | 'dark';
	duration?: number;
	hasCloseBtn?: boolean;
	class?: string;
	pauseOnHover?: boolean;
	link?: string;
};

const notification = (userOptions: configNotification) => {
	const positionGravity = userOptions.position ? userOptions.position.split('-') : '';

	const position: 'left' | 'center' | 'right' | undefined = positionGravity[0] as 'left' | 'center' | 'right' | undefined;
	const gravity: 'top' | 'bottom' | undefined = positionGravity[1] as 'top' | 'bottom' | undefined;

	const options: Options = {
		duration: userOptions.duration || 5000,
		gravity,
		position,
		text: userOptions.text || 'This is a message',
		newWindow: true,
		close: userOptions.hasCloseBtn || false,
		backgroundColor: '',
		className: userOptions.class || '',
		stopOnFocus: userOptions.pauseOnHover || true
	};

	if (userOptions.link) {
		options.destination = userOptions.link;
	}

	if (userOptions.variant) {
		options.className = `${options.className} ${userOptions.variant}`;
	}

	const toastify = Toastify(options);
	toastify.showToast();
	return toastify;
};

export const removeNotif = (toastId: string) => {
	document.querySelector(`.toast.toast-${toastId}`)?.remove();
};

export const notifError = (message: string, duration = 3000) => {
	return toastify(message, duration, 'error');
};

export const notifSuccess = (message: string, duration = 3000) => {
	return toastify(message, duration, 'success');
};

export const notifInfo = (message: string, duration = 3000) => {
	return toastify(message, duration, 'info');
};

export const notifErrorV2 = (message: string, duration = 3000) => {
	return notification({ text: message, duration: duration, variant: 'error', position: 'right-top', hasCloseBtn: true });
};

export const notifSuccessV2 = (message: string, duration = 3000) => {
	return notification({ text: message, duration: duration, variant: 'success', position: 'right-top', hasCloseBtn: true });
};

export const notifInfoV2 = (message: string, duration = 3000) => {
	return notification({ text: message, duration: duration, variant: 'info', position: 'right-top', hasCloseBtn: true });
};

export const notifWarningV2 = (message: string, duration = 3000) => {
	return notification({ text: message, duration: duration, variant: 'warning', position: 'right-top', hasCloseBtn: true });
};

export const notifErrorV3 = (message: string, submessage = '', duration = 3000) => {
	return toastifyV2(message, submessage, duration, 'error');
};

export const notifSuccessV3 = (message: string, submessage = '', duration = 3000) => {
	return toastifyV2(message, submessage, duration, 'success');
};
